import { For, Show } from "solid-js";
import { Icon } from "~/icon";
import { switchPatientLocale, usePatientText } from "./i18n";

// A page that changes language in place, without a reload, says which language it shows now and
// takes the pick itself: the booking wizard, whose reload would lose everything the patient typed.
// Every other page reloads with the pick (switchPatientLocale).
type LanguagePickProps = {
  current?: string;
  onPick?: (code: string) => void;
};

// The language row above a patient page's card: the browser's language offered in its own words
// when the page opened in another one, and a picker that names every language in itself
// ("Español", not "Spanish"), so a patient who can't read the current page can still find theirs.
export function PatientLanguageBar(props: LanguagePickProps) {
  const { i18n } = usePatientText();
  const current = () => props.current ?? i18n.locale;
  const pick = (code: string) => (props.onPick ?? switchPatientLocale)(code);

  return (
    <div class="mb-3 flex flex-wrap items-center justify-end gap-2">
      <Show when={i18n.suggestedLocale?.code !== current() && i18n.suggestedLocale}>
        {(suggested) => (
          <button
            type="button"
            lang={suggested().code}
            class="mr-auto flex min-h-11 items-center gap-1.5 rounded-full bg-brand-light px-4 text-sm font-medium text-brand-link hover:underline"
            onClick={() => pick(suggested().code)}
          >
            <Icon name="globe" size="sm" />
            {suggested().prompt}
          </button>
        )}
      </Show>
      <PatientLanguagePicker current={props.current} onPick={props.onPick} />
    </div>
  );
}

export function PatientLanguagePicker(props: LanguagePickProps) {
  const { t, i18n } = usePatientText();
  const current = () => props.current ?? i18n.locale;

  return (
    <label class="flex items-center gap-1.5 text-typography-secondary">
      <Icon name="globe" size="sm" />
      <span class="sr-only">{t("common.language")}</span>
      {/* ui-select opts out of the legacy global select reset (forms.css), which forces bare
          selects to w-full and restyles them. 16px text on phones so iOS doesn't zoom on focus. */}
      <select
        class="ui-select min-h-11 rounded-full border border-brand-subtle bg-white py-1 pr-9 pl-3 text-base font-medium text-typography-secondary transition-colors hover:border-brand-primary sm:text-sm"
        onChange={(e) => (props.onPick ?? switchPatientLocale)(e.currentTarget.value)}
      >
        <For each={i18n.locales}>
          {(option) => (
            <option value={option.code} lang={option.code} selected={option.code === current()}>
              {option.name}
            </option>
          )}
        </For>
      </select>
    </label>
  );
}
